<template>
  <div class="flex-1 flex flex-col min-w-0 min-h-0 p-4 overflow-auto">
    <div class="flex items-center justify-between mb-4">
      <div>
        <div class="flex items-center gap-2">
          <h3 class="text-lg font-semibold text-gray-900">
            Times &amp; Targets
          </h3>

          <span
            v-if="isProcurementComplete"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
          >
            Complete
          </span>
        </div>

        <p class="text-sm text-gray-500">
          Target dates are calculated backwards from the package contract start date.
        </p>

        <p class="text-sm text-gray-500 mt-1">
          If the last step has an actual date filled, procurement is marked as complete.
        </p>
      </div>

      <button
        v-if="canSave"
        type="button"
        class="btn btn--primary-blue"
        :disabled="updateAction.loading.value || packageUpdateAction.loading.value"
        data-test="save-procurement-steps"
        @click="saveSteps"
      >
        Save
      </button>
    </div>

    <div class="mb-4 max-w-xs">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        {{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.contractStartDate') }}
      </label>

      <AppInput
        v-model="contractStartDate"
        name="contract_start_date"
        type="date"
        class="form-input"
        :disabled="!canSave"
      />
    </div>

    <div
      v-if="indexAction.loading.value"
      class="flex justify-center py-12"
    >
      <AppSpinner />
    </div>

    <table
      v-else
      class="min-w-full divide-y divide-gray-200"
    >
      <thead class="bg-gray-50">
        <tr>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Step
          </th>

          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Target Date
          </th>

          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Actual Date
          </th>

          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Lead Time Weeks
          </th>

          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Days Remaining
          </th>

          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Status
          </th>
        </tr>
      </thead>

      <tbody class="bg-white divide-y divide-gray-200">
        <tr
          v-for="(step, index) in stepsForm.form.steps"
          :key="step.id ?? index"
        >
          <td class="px-3 py-2">
            <AppInput
              v-model="step.name"
              type="text"
              :name="`step-name-${index}`"
              class="form-input"
              :disabled="!canSave"
            />
          </td>

          <td class="px-3 py-2 text-sm text-gray-700">
            {{ formatDisplayDate(targetDateFor(index)) }}
          </td>

          <td class="px-3 py-2">
            <AppInput
              v-model="step.actual_date"
              :name="`step-actual-${index}`"
              type="date"
              class="form-input"
              :disabled="!canSave"
            />
          </td>

          <td class="px-3 py-2">
            <AppInput
              v-model="step.lead_time_weeks"
              :name="`step-lead-${index}`"
              type="number"
              class="form-input w-24"
              :disabled="!canSave"
            />
          </td>

          <td class="px-3 py-2 text-sm text-gray-700">
            {{ daysRemainingFor(index) }}
          </td>

          <td
            class="px-3 py-2 text-sm"
            :class="{ 'text-red-600 font-medium': statusFor(index) === 'Urgent' }"
          >
            {{ statusFor(index) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput } from '@/helpers';
import Package from '@/models/Package';
import { usePackageProcurementStepsStore } from '@/store/packageProcurementSteps';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  selectedPackage: Package;
  canSave: boolean;
}>();

const projectsStore = useProjectsStore();
const packagesStore = usePackagesStore();
const packageProcurementStepsStore = usePackageProcurementStepsStore();
const indexAction = useStoreApiAction(packageProcurementStepsStore.index);
const updateAction = useStoreApiAction(packageProcurementStepsStore.update);
const packageUpdateAction = useStoreApiAction(packagesStore.update);
const contractStartDate = ref<string | undefined>(undefined);

const stepsForm = useFormData({
  steps: [] as {
    id?: number;
    name: string;
    lead_time_weeks: number;
    actual_date?: string;
    sort_order?: number;
  }[],
});

const packageSteps = computed(() => {
  return packageProcurementStepsStore.models
    .where('package_id', props.selectedPackage.id)
    .orderBy('sort_order')
    .get();
});

const usingProjectDefaults = computed(() => !packageSteps.value.length);

const lastStepComplete = computed(() => {
  const steps = stepsForm.form.steps;

  if(!steps.length) {
    return false;
  }

  return !!steps[steps.length - 1].actual_date;
});

const isProcurementComplete = computed(() => {
  return !!props.selectedPackage.subcontractor_id && lastStepComplete.value;
});

const startDate = computed(() => {
  return contractStartDate.value ||
    projectsStore.project?.contract_start_date;
});

const populateSteps = () => {
  if(packageSteps.value.length) {
    stepsForm.form.steps = packageSteps.value.map((step) => ({
      id: step.id,
      name: step.name,
      lead_time_weeks: step.lead_time_weeks,
      actual_date: dateForDateInput(step.actual_date),
      sort_order: step.sort_order,
    }));

    return;
  }

  const projectSteps = projectsStore.project?.subcontract_settings?.procurement_steps ?? [];

  stepsForm.form.steps = projectSteps.map((step, index) => ({
    name: step.name,
    lead_time_weeks: step.lead_time_weeks,
    actual_date: undefined,
    sort_order: index,
  }));
};

const targetDateFor = (index: number): string | undefined => {
  const anchor = startDate.value;

  if(!anchor || !stepsForm.form.steps.length) {
    return undefined;
  }

  let cursor = DateTime.fromISO(anchor).startOf('day');

  for(let i = stepsForm.form.steps.length - 1;i > index;i -= 1) {
    cursor = cursor.minus({ weeks: Number(stepsForm.form.steps[i].lead_time_weeks ?? 0) });
  }

  return cursor.toISODate();
};

const daysRemainingFor = (index: number): number | undefined => {
  if(lastStepComplete.value) {
    return 0;
  }

  const targetDate = targetDateFor(index);

  if(!targetDate) {
    return undefined;
  }

  return Math.round(
    DateTime.fromISO(targetDate).diff(DateTime.now().startOf('day'), 'days').days,
  );
};

const statusFor = (index: number): string => {
  if(stepsForm.form.steps[index].actual_date) {
    return 'Closed';
  }

  if(lastStepComplete.value) {
    return 'Pending';
  }

  const daysRemaining = daysRemainingFor(index);

  if(daysRemaining !== undefined && daysRemaining <= 7) {
    return 'Urgent';
  }

  return 'Pending';
};

const formatDisplayDate = (value?: string | undefined) => {
  if(!value) {
    return '-';
  }

  return DateTime.fromISO(value).toFormat('dd-MMM-yyyy');
};

const loadSteps = () => {
  if(!projectsStore.project?.id || !props.selectedPackage?.id) {
    return;
  }

  indexAction.request(
    projectsStore.project.id,
    props.selectedPackage.id,
  ).then(() => {
    populateSteps();
  });
};

const saveSteps = () => {
  if(!projectsStore.project?.id) {
    return;
  }

  const payload = stepsForm.form.steps.map((step, index) => ({
    id: usingProjectDefaults.value ? undefined : step.id,
    name: step.name,
    lead_time_weeks: Number(step.lead_time_weeks),
    actual_date: step.actual_date || undefined,
    sort_order: index,
  }));

  packageUpdateAction.request(
    props.selectedPackage.id,
    { contract_start_date: contractStartDate.value || undefined },
  ).then(() => {
    return updateAction.request(
      projectsStore.project!.id,
      props.selectedPackage.id,
      payload,
    );
  }).then(() => {
    populateSteps();

    toast.success('Times & Targets saved successfully');
  }).catch((error) => {
    if(error?.response?.status === 422) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to save',
        text: error?.data?.message,
      });
    }
  });
};

watch(() => props.selectedPackage, (pkg) => {
  contractStartDate.value = dateForDateInput(pkg?.contract_start_date) ||
    dateForDateInput(projectsStore.project?.contract_start_date);
}, { immediate: true });

watch(() => [props.selectedPackage.id, props.selectedPackage.subcontractor_id], () => {
  loadSteps();
}, { immediate: true });
</script>
